import {
  apiClient,
  ErrorPage,
  FailedIdentificationFinalPage,
  FilteredCreditApplication,
  Loader,
  reportErrorToConsole,
  useDownloadVouchedReport,
  useFeatureFlags,
  useGetIdentityVerificationSession,
  VouchedCallbacks,
  VouchedComponent,
  VouchedJob,
} from '@applyonline/shared'
import { EApplicantType, EFinancingProgram } from '@if/core/types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useNavigate } from 'react-router-dom'

interface Props {
  creditApp: FilteredCreditApplication
  applicantType: EApplicantType
}

const IdentityVerificationPage = ({ creditApp, applicantType }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [flags] = useFeatureFlags(EFinancingProgram.Personal)
  const [session, isLoadingIdentityVerif] = useGetIdentityVerificationSession(creditApp.id, EFinancingProgram.Personal)
  const [downloadVouchedReport] = useDownloadVouchedReport()
  const [simScenario, setSimScenario] = useState('success')

  const callbacks = useMemo<VouchedCallbacks>(
    () => ({
      onJobValid: (_job: VouchedJob) => {
        navigate(`/creditapplication/${creditApp.id}`, { replace: true })
      },
      onJobInvalid: (job: VouchedJob) => {
        downloadVouchedReport({
          creditApplicationId: creditApp.id,
          vouchedJobToken: job.token,
          financingProgram: EFinancingProgram.Personal,
          applicantType,
        }).catch(reportErrorToConsole)
        navigate(`/creditapplication/${creditApp.id}`, { replace: true })
      },
      onCameraNoPermission: () => {
        navigate(`/creditapplication/${creditApp.id}`, { replace: true })
      },
    }),
    [applicantType, creditApp.id, downloadVouchedReport, navigate],
  )

  // Feature switch: feature is fully unavailable when off.
  if (flags && !flags.enableVouchedForThinCreditB2B)
    return <Navigate to={`/creditapplication/${creditApp.id}`} replace />

  // LOCAL ONLY (dev/testing): simulate Vouched via the proxy instead of the real SDK — no camera,
  // no identity session. Triggers the backend DownloadVouchedReport -> proxy -> creates the
  // ValidPhotoId document. Off unless VITE_USE_VOUCHED_PROXY is set. Remove before finalizing.
  if (import.meta.env.VITE_USE_VOUCHED_PROXY) {
    const simulate = (token: string) =>
      apiClient
        .post(`${EFinancingProgram.Personal}/creditapplication/${creditApp.id}/downloadVouchedReport`, {
          vouchedJobToken: token,
          applicantType,
        })
        .catch(reportErrorToConsole)
        .finally(() => navigate(`/creditapplication/${creditApp.id}`, { replace: true }))

    return (
      <main className="general-message" style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '2rem' }}>
        <h1>Simulated Vouched (local proxy)</h1>
        <p>No camera or real Vouched — pick the scenario the backend proxy returns.</p>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', maxWidth: '380px' }}>
          Scenario
          <select value={simScenario} onChange={(e) => setSimScenario(e.target.value)}>
            <option value="success">Success (clean pass)</option>
            <option value="name-mismatch">Name mismatch (NameMatchError)</option>
            <option value="face-fail">Face match fail (FaceMatchError)</option>
            <option value="dob-mismatch">DOB mismatch (BirthDateMatchError)</option>
            <option value="expired-id">Expired ID (ExpiredIdError)</option>
            <option value="invalid-id">Invalid ID (InvalidIdError)</option>
            <option value="liveness">Liveness skipped (stays AwaitingApproval)</option>
            <option value="addr-mismatch">Address mismatch (VerifyAddress task)</option>
          </select>
        </label>
        <div className="btn-group" style={{ display: 'flex', gap: '1rem' }}>
          <button type="button" className="btn btn-blue" onClick={() => simulate(simScenario)}>
            Run scenario
          </button>
        </div>
      </main>
    )
  }

  if (isLoadingIdentityVerif)
    return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />

  const vouchedFailedCount = creditApp.applicant?.vouchedFailedCount || 0
  if (vouchedFailedCount > 1) return <FailedIdentificationFinalPage creditApp={creditApp} />

  if (!creditApp) return <ErrorPage />

  // Personal applications intentionally show no logo in the Vouched widget (B2C keeps its logo).
  // Passing an empty logoUrl makes the shared config omit the logo entirely (no placeholder).
  return (
    <VouchedComponent
      session={session}
      creditApplication={creditApp}
      logoUrl=""
      callbacks={callbacks}
      applicantType={applicantType}
    />
  )
}

export default IdentityVerificationPage
